import React from 'react'
import * as PhosphorIcons from '@phosphor-icons/react'

const KeywordOptimizerViewResult = ({result}) => {
  const getQualityScoreColor = (score) => {
    switch(score?.toLowerCase()) {
      case 'high':
        return 'bg-success/20 text-success';
      case 'medium':
        return 'bg-warning/20 text-warning';
      case 'low':
        return 'bg-error/20 text-error';
      default:
        return 'bg-base-200 text-base-content';
    }
  };

  const getMarketTrendIcon = (trend) => {
    switch(trend?.toLowerCase()) {
      case 'rising':
        return <PhosphorIcons.TrendUp size={16} className="text-success" />;
      case 'falling':
        return <PhosphorIcons.TrendDown size={16} className="text-error" />;
      case 'stable':
        return <PhosphorIcons.Minus size={16} className="text-warning" />;
      default:
        return <PhosphorIcons.Question size={16} className="text-base-content" />;
    }
  };

  return (
    <div className="mt-4 overflow-x-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Keyword Validation Section */}
        <div className="space-y-4">
          {/* Validation Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-primary/10">
                  <PhosphorIcons.MagnifyingGlass size={20} weight="bold" className="text-primary"/>
                </div>
                <h3 className="card-title text-lg">Keyword Validation</h3>
              </div>

              <div className="space-y-3">
                {/* Validation Status */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      result?.analysis?.validation?.is_valid 
                        ? 'bg-success/20 text-success' 
                        : 'bg-error/20 text-error'
                    }`}>
                      {result?.analysis?.validation?.is_valid ? 'Valid' : 'Invalid'}
                    </span>
                  </div>
                </div>

                {/* Quality Score */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Quality Score</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getQualityScoreColor(result?.analysis?.validation?.quality_score)}`}>
                      {result?.analysis?.validation?.quality_score}
                    </span>
                  </div>
                </div>

                {/* Keyword Type */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Type</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-info/20 text-info capitalize">
                      {result?.analysis?.validation?.keyword_type}
                    </span>
                  </div>
                </div>

                {/* Confidence */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">Confidence</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-success/20 text-success">
                      {Math.round(result?.analysis?.validation?.confidence * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-base-300 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-success"
                      style={{width: `${result?.analysis?.validation?.confidence * 100}%`}}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Semantic Clusters Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-secondary/10">
                  <PhosphorIcons.Graph size={20} weight="bold" className="text-secondary"/>
                </div>
                <h3 className="card-title text-lg">Semantic Clusters</h3>
              </div>

              <div className="flex flex-wrap gap-2">
                {result?.analysis?.keyword_status?.semantic_clusters?.map((cluster, index) => (
                  <span key={index} className="px-3 py-1.5 bg-secondary/10 text-secondary rounded-full text-sm">
                    {cluster}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Keyword Status and Suggestions Section */}
        <div className="space-y-4">
          {/* Keyword Status Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-info/10">
                  <PhosphorIcons.ChartBar size={20} weight="bold" className="text-info"/>
                </div>
                <h3 className="card-title text-lg">Keyword Metrics</h3>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Search Intent */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="text-sm text-base-content/70">Search Intent</span>
                  <div className="font-medium capitalize mt-1">
                    {result?.analysis?.keyword_status?.search_intent || 'N/A'}
                  </div>
                </div>

                {/* Search Volume */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="text-sm text-base-content/70">Search Volume</span>
                  <div className="font-medium capitalize mt-1">
                    {result?.analysis?.keyword_status?.search_volume || 'N/A'}
                  </div>
                </div>

                {/* Difficulty Score */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="text-sm text-base-content/70">Difficulty</span>
                  <div className="font-medium mt-1">
                    {result?.analysis?.keyword_status?.difficulty_score}/100
                  </div>
                </div>

                {/* Competition Level */}
                <div className="p-3 bg-base-200 rounded-lg">
                  <span className="text-sm text-base-content/70">Competition</span>
                  <div className="font-medium capitalize mt-1">
                    {result?.analysis?.keyword_status?.competition_level || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Suggested Variations Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-warning/10">
                  <PhosphorIcons.ListPlus size={20} weight="bold" className="text-warning"/>
                </div>
                <h3 className="card-title text-lg">Suggested Variations</h3>
              </div>

              <div className="space-y-2">
                {result?.analysis?.keyword_status?.suggested_variations?.map((variation, index) => (
                  <div key={index} className="p-2 bg-base-200 rounded-lg text-sm">
                    {variation}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Metadata Card */}
          <div className="card bg-base-100 shadow-sm">
            <div className="card-body p-4">
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 rounded-lg bg-success/10">
                  <PhosphorIcons.ChartLine size={20} weight="bold" className="text-success"/>
                </div>
                <h3 className="card-title text-lg">Market Insights</h3>
              </div>

              <div className="space-y-3">
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Market Trend</span>
                    <div className="flex items-center gap-2">
                      {getMarketTrendIcon(result?.metadata?.market_trend)}
                      <span className="capitalize">{result?.metadata?.market_trend}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeywordOptimizerViewResult;